import React, { useState } from "react";
import { Icon } from "../icons";
import { fmtDateTime } from "../lib/format";
import { SectionLabel } from "./fields";

/** This job's escalations, newest last, so the MC sees what is being resolved. */
export const JobEscalationList = ({ escalations }) =>
  escalations.length ? (
    <>
      <SectionLabel icon="alert">Escalations on this Move</SectionLabel>
      {escalations.map((e) => (
        <div className="note-item" key={e.id}>
          <span className={`badge ${e.status}`}>
            <span className="bdot" />
            {e.status}
          </span>{" "}
          {e.note}
          <div className="meta">
            {e.type} · {fmtDateTime(e.createdAt)}
            {e.status === "resolved" && e.resolvedAt ? " · resolved " + fmtDateTime(e.resolvedAt) : ""}
          </div>
          {e.resolutionNote ? <div className="meta">Resolution: {e.resolutionNote}</div> : null}
        </div>
      ))}
    </>
  ) : null;

const teamLabel = (type) => (type === "ops" ? "Operations" : "Sales");

/**
 * "Post escalation notes" + "Escalation resolved": resolves every open escalation on the job.
 * Says which escalations are open (Operations or Sales) and confirms when the notes are saved
 * (Asana 1220099429071097, 4.4).
 */
export const PostEscalationNotes = ({ job, escalations = [], actions }) => {
  const [notes, setNotes] = useState(job.postEscalationNotes || "");
  const [justSaved, setJustSaved] = useState(false);
  const open = escalations.filter((e) => e.status === "open");
  const dirty = notes !== (job.postEscalationNotes || "");
  const canResolve = open.length > 0 && notes.trim() !== "";
  const teams = Array.from(new Set(open.map((e) => teamLabel(e.type))));

  return (
    <div className="field" style={{ marginBottom: 14 }}>
      <label htmlFor={`post-esc-${job.id}`}>Post escalation notes</label>
      <textarea
        id={`post-esc-${job.id}`}
        rows={3}
        placeholder="Summarise how the escalation was resolved..."
        value={notes}
        onChange={(e) => {
          setNotes(e.target.value);
          setJustSaved(false);
        }}
      />
      <div className="toolbar-row" style={{ marginTop: 6, alignItems: "center" }}>
        <button
          type="button"
          className={`btn small${justSaved ? " success" : ""}`}
          disabled={!dirty}
          onClick={() => {
            actions.setJobField(job.id, "postEscalationNotes", notes);
            setJustSaved(true);
          }}
        >
          <Icon name={justSaved ? "check" : "note"} />
          {justSaved ? "Notes saved" : "Save notes"}
        </button>
        <button
          type="button"
          className="btn small success"
          disabled={!canResolve}
          onClick={() => actions.resolveJobEscalations(job.id, notes.trim())}
        >
          <Icon name="check" />
          Escalation resolved
        </button>
        {open.length === 0 ? (
          <span className="pm-hint">No open escalations</span>
        ) : (
          <span className="pm-hint" style={{ color: "var(--danger)" }}>
            {open.length} open escalation{open.length > 1 ? "s" : ""} ({teams.join(", ")})
            {notes.trim() === "" ? " · add the resolution notes first" : ""}
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * "Raise a New Escalation": Operations or Sales, what is happening, Send. Shared by In Progress
 * and the LD Delivery Day (prototype, Asana 1220099429071097, item 6). `stage` "delivery" sends
 * it to the delivery branch's channel.
 */
export const RaiseEscalationPanel = ({ job, actions, stage }) => {
  const [type, setType] = useState("ops");
  const [text, setText] = useState("");
  const [sent, setSent] = useState(false);

  const typeStyle = (t) => {
    const active = type === t;
    const color = t === "ops" ? "var(--danger)" : "var(--purple)";
    return {
      flex: 1,
      justifyContent: "center",
      background: active ? color : undefined,
      color: active ? "#fff" : undefined,
      borderColor: active ? color : undefined,
    };
  };

  return (
    <div className="panel" style={{ marginBottom: 14 }}>
      <div className="panel-head">
        <Icon name="alert" />
        Raise a New Escalation
      </div>
      <div className="panel-body" style={{ maxHeight: "none", padding: "12px 14px 14px" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          {[
            ["ops", "Operations"],
            ["sales", "Sales"],
          ].map(([t, label]) => (
            <button
              key={t}
              type="button"
              className="btn small esc-type-btn"
              aria-pressed={type === t}
              style={typeStyle(t)}
              onClick={() => setType(t)}
            >
              <Icon name="alert" />
              {label}
            </button>
          ))}
        </div>
        <textarea
          aria-label="New escalation"
          placeholder="What's happening? Be specific so ops/sales can act fast."
          rows={5}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setSent(false);
          }}
        />
        <div className="toolbar-row">
          <button
            className={`btn small ${sent ? "success" : "primary"}`}
            type="button"
            disabled={!text.trim()}
            onClick={() => {
              if (!text.trim()) return;
              if (stage) actions.raiseEscalation(job.id, type, text.trim(), stage);
              else actions.raiseEscalation(job.id, type, text.trim());
              setText("");
              setSent(true);
            }}
          >
            <Icon name={sent ? "check" : "alert"} />
            {sent ? "Escalation sent" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
};
